import type { ComponentPropsWithRef, ReactNode } from 'react'
import { Icon, IconNameEnum } from '@/React/Shared/Component/Icon'

export interface BreadCrumbsContainerProps extends ComponentPropsWithRef<'ol'> {
    children?: ReactNode
}

export function BreadCrumbsContainer({
    children,
    className = '',
    ...restProps
}: BreadCrumbsContainerProps) {
    return (
        <nav
            role="navigation"
            aria-label="Breadcrumb"
        >
            <ol
                {...restProps}
                className={`flex items-center gap-2 whitespace-nowrap min-w-0 ${className}`.trim()}
            >
                {children}
            </ol>
        </nav>
    )
}

export interface BreadCrumbsItemProps extends ComponentPropsWithRef<'li'> {
    url?: string | null
    isCurrent?: boolean
    children: ReactNode
}

export function BreadCrumbsItem({
    url,
    isCurrent = false,
    children,
    className = '',
    ...restProps
}: BreadCrumbsItemProps) {
    if (url) {
        return (
            <li
                {...restProps}
                aria-current={isCurrent ? 'page' : undefined}
                className={`text-sm flex items-center text-gray-500 hover:text-blue-600 ${className}`.trim()}
            >
                <a href={url}>{children}</a>
            </li>
        )
    }

    return (
        <li
            {...restProps}
            aria-current={isCurrent ? 'page' : undefined}
            className={`text-sm flex items-center text-gray-500 cursor-default ${className}`.trim()}
        >
            {children}
        </li>
    )
}

export interface BreadCrumbsSeparatorProps extends ComponentPropsWithRef<'li'> {}

export function BreadCrumbsSeparator({
    className = '',
    ...restProps
}: BreadCrumbsSeparatorProps) {
    return (
        <li
            aria-hidden="true"
            className={`inline-flex items-center ${className}`.trim()}
            {...restProps}
        >
            <Icon
                name={IconNameEnum.AngleRight}
                className="h-3 w-3 text-gray-400 shrink-0"
                aria-hidden="true"
            />
        </li>
    )
}

export const BreadCrumbs = Object.assign(BreadCrumbsContainer, {
    Container: BreadCrumbsContainer,
    Item: BreadCrumbsItem,
    Separator: BreadCrumbsSeparator,
})
